import { Download, History, LoaderCircle, RefreshCw, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, downloadFile } from "../api";
import { EmptyState, LoadingState, MetricStrip, PageHeader, Pagination, SectionHeading, StatusBadge, formatDate } from "../components";
import { useAppData } from "../context";
import { CompetitorCollectionPanel } from "../CompetitorCollectionPanel";
import type { CatalogCompetitionItem, CommerceInsightKind, CommerceInsightRun, PriceObservation, StockRiskItem, StockRiskLevel } from "../types";

const PAGE_SIZE = 30;
type View = CommerceInsightKind | "PRICE_HISTORY";

const riskLabels: Record<StockRiskLevel, string> = {
  CRITICAL: "紧急",
  HIGH: "高风险",
  MEDIUM: "中风险",
  LOW: "低风险",
  NO_SALES: "无近期销量",
};

const priceSourceLabels: Record<PriceObservation["source"], string> = {
  READINESS: "商品回读",
  CATALOG_PRICE: "目录竞争价",
  TASK_BEFORE: "任务修改前",
  TASK_VERIFIED: "任务回读",
  MERCADO_LIBRE_HISTORY: "官方历史",
};

function progress(run: CommerceInsightRun | null) {
  if (!run || run.totalCount <= 0) return 0;
  return Math.min(100, Math.round((run.processedCount / run.totalCount) * 100));
}

function formatPrice(currency: string, value: number | null) {
  return value === null ? "--" : `${currency ? `${currency} ` : ""}${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function RiskRail({ summary, selected, onSelect }: { summary: Partial<Record<StockRiskLevel, number>>; selected: string; onSelect(value: string): void }) {
  const levels = Object.keys(riskLabels) as StockRiskLevel[];
  const total = levels.reduce((sum, level) => sum + (summary[level] ?? 0), 0);
  return <div className="risk-rail" aria-label="缺货风险分布">
    {levels.map((level) => {
      const count = summary[level] ?? 0;
      return <button key={level} type="button" className={`${level.toLowerCase()} ${selected === level ? "selected" : ""}`} onClick={() => onSelect(selected === level ? "" : level)}>
        <span>{riskLabels[level]}</span><strong>{count.toLocaleString()}</strong><small>{total ? `${Math.round((count / total) * 100)}%` : "0%"}</small>
      </button>;
    })}
  </div>;
}

export function CommerceInsights() {
  const { data } = useAppData();
  const [view, setView] = useState<View>("STOCK_RISK");
  const [accountCode, setAccountCode] = useState("");
  const [runs, setRuns] = useState<CommerceInsightRun[]>([]);
  const [selectedRuns, setSelectedRuns] = useState<Partial<Record<CommerceInsightKind, string>>>({});
  const [stockItems, setStockItems] = useState<StockRiskItem[]>([]);
  const [catalogItems, setCatalogItems] = useState<CatalogCompetitionItem[]>([]);
  const [priceItems, setPriceItems] = useState<PriceObservation[]>([]);
  const [summary, setSummary] = useState<Partial<Record<StockRiskLevel, number>>>({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [siteCode, setSiteCode] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!accountCode && data?.accounts[0]) setAccountCode(data.accounts[0].accountCode);
  }, [accountCode, data]);

  const selectedRun = useMemo(() => {
    if (view === "PRICE_HISTORY") return null;
    const selectedId = selectedRuns[view];
    return runs.find((run) => run.id === selectedId) ?? runs.find((run) => run.kind === view) ?? null;
  }, [runs, selectedRuns, view]);

  const loadRuns = useCallback(async () => {
    if (!accountCode) { setRuns([]); return []; }
    const result = await api.commerceInsightRuns(accountCode);
    setRuns(result.items);
    setSelectedRuns((current) => ({
      STOCK_RISK: result.items.some((run) => run.id === current.STOCK_RISK) ? current.STOCK_RISK : result.items.find((run) => run.kind === "STOCK_RISK")?.id,
      CATALOG_PRICE: result.items.some((run) => run.id === current.CATALOG_PRICE) ? current.CATALOG_PRICE : result.items.find((run) => run.kind === "CATALOG_PRICE")?.id,
    }));
    return result.items;
  }, [accountCode]);

  const loadRows = useCallback(async () => {
    if (!accountCode) return;
    setLoading(true);
    setMessage("");
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (siteCode) params.set("siteCode", siteCode);
    if (status) params.set(view === "STOCK_RISK" ? "risk" : "status", status);
    if (query) params.set("search", query);
    try {
      if (view === "PRICE_HISTORY") {
        params.set("accountCode", accountCode);
        const result = await api.priceHistory(params);
        setPriceItems(result.items); setTotal(result.total);
      } else if (selectedRun?.kind === "STOCK_RISK") {
        const result = await api.stockRisk(selectedRun.id, params);
        setStockItems(result.items); setSummary(result.summary); setTotal(result.total);
      } else if (selectedRun?.kind === "CATALOG_PRICE") {
        const result = await api.catalogPrice(selectedRun.id, params);
        setCatalogItems(result.items); setTotal(result.total);
      } else {
        setTotal(0); setStockItems([]); setCatalogItems([]);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "经营分析加载失败。");
    } finally { setLoading(false); }
  }, [accountCode, page, query, selectedRun?.id, selectedRun?.kind, siteCode, status, view]);

  useEffect(() => {
    if (!accountCode) return;
    setLoading(true);
    void loadRuns().catch((error) => {
      setMessage(error instanceof Error ? error.message : "分析记录加载失败。");
      setLoading(false);
    });
  }, [accountCode, loadRuns]);

  useEffect(() => { void loadRows(); }, [loadRows]);

  useEffect(() => {
    if (!selectedRun || selectedRun.status !== "RUNNING") return;
    let stopped = false;
    const poll = async () => {
      try {
        const next = await api.commerceInsightRun(selectedRun.id);
        if (stopped) return;
        setRuns((current) => current.map((run) => run.id === next.id ? next : run));
        if (next.status !== "RUNNING") {
          await loadRuns();
          await loadRows();
          if (!stopped) setMessage(`${next.kind === "STOCK_RISK" ? "缺货风险" : "目录竞争价"}分析已完成。`);
        }
      } catch (error) {
        if (!stopped) setMessage(error instanceof Error ? error.message : "分析进度读取失败。");
      }
    };
    const timer = window.setInterval(() => void poll(), 1200);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [loadRows, loadRuns, selectedRun]);

  function resetFilters(nextView?: View) {
    if (nextView) setView(nextView);
    setPage(1); setSiteCode(""); setStatus(""); setSearch(""); setQuery(""); setTotal(0);
  }

  async function start(kind: CommerceInsightKind) {
    if (!accountCode) return;
    setBusy(kind); setMessage("");
    try {
      const run = await api.startCommerceInsight(accountCode, kind, kind === "STOCK_RISK" ? 90 : undefined);
      setRuns((current) => [run, ...current.filter((entry) => entry.id !== run.id)]);
      setSelectedRuns((current) => ({ ...current, [kind]: run.id }));
      setMessage(`${kind === "STOCK_RISK" ? "缺货风险" : "目录竞争价"}分析已进入后台队列。`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "分析无法启动。");
    } finally { setBusy(""); }
  }

  async function exportCurrent() {
    if (!selectedRun) return;
    setBusy("EXPORT"); setMessage("");
    try {
      await downloadFile(`/api/commerce-insights/runs/${encodeURIComponent(selectedRun.id)}/export`, `meliforge-${selectedRun.kind.toLowerCase()}-${selectedRun.id}.xlsx`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "分析结果导出失败。");
    } finally { setBusy(""); }
  }

  async function syncPriceHistory(item: PriceObservation) {
    setBusy(item.id); setMessage("");
    try {
      const result = await api.syncOfficialPriceHistory({
        accountCode: item.accountCode, siteCode: item.siteCode, merchantSku: item.merchantSku,
        parentItemId: item.parentItemId, childItemId: item.childItemId, currencyId: item.currencyId,
      });
      setMessage(`官方价格历史读取 ${result.received} 条，保存 ${result.stored} 条。`);
      await loadRows();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "官方价格历史读取失败。");
    } finally { setBusy(""); }
  }

  if (!data) return <LoadingState />;
  const allSites = [...new Set([
    ...stockItems.map((item) => item.siteCode),
    ...catalogItems.map((item) => item.siteCode),
    ...priceItems.map((item) => item.siteCode),
    ...data.accounts.flatMap((account) => account.sites),
  ])].filter(Boolean).sort();
  const stockRunCount = runs.filter((run) => run.kind === "STOCK_RISK").length;
  const catalogRunCount = runs.filter((run) => run.kind === "CATALOG_PRICE").length;
  const currentProgress = progress(selectedRun);
  const latestFreshness = view === "STOCK_RISK" ? stockItems[0]?.dataFreshAt : view === "CATALOG_PRICE" ? catalogItems[0]?.capturedAt : priceItems[0]?.observedAt;

  return <>
    <PageHeader eyebrow="COMMERCE / READ-ONLY SIGNALS" title="经营分析" actions={<div className="page-action-cluster">
      <select aria-label="店铺" value={accountCode} onChange={(event) => { setAccountCode(event.target.value); resetFilters(); setRuns([]); setSelectedRuns({}); }}>
        {data.accounts.map((account) => <option key={account.id} value={account.accountCode}>{account.accountCode}</option>)}
      </select>
      {view !== "PRICE_HISTORY" ? <button className="button primary" disabled={!accountCode || busy !== "" || selectedRun?.status === "RUNNING"} onClick={() => void start(view)}>
        {busy === view ? <LoaderCircle size={17} className="spin" /> : <RefreshCw size={17} />}刷新分析
      </button> : null}
      {view !== "PRICE_HISTORY" ? <button className="button secondary" disabled={!selectedRun || !["SUCCESS", "PARTIAL"].includes(selectedRun.status) || busy !== ""} onClick={() => void exportCurrent()}>
        {busy === "EXPORT" ? <LoaderCircle size={17} className="spin" /> : <Download size={17} />}导出 Excel
      </button> : null}
    </div>} />
    {message ? <div className="inline-message" role="status">{message}</div> : null}

    <CompetitorCollectionPanel accountCode={accountCode} />

    <div className="insight-tabs" role="tablist" aria-label="经营分析类型">
      <button role="tab" aria-selected={view === "STOCK_RISK"} className={view === "STOCK_RISK" ? "active" : ""} onClick={() => resetFilters("STOCK_RISK")}><span>库存覆盖</span><small>{stockRunCount} 次分析</small></button>
      <button role="tab" aria-selected={view === "CATALOG_PRICE"} className={view === "CATALOG_PRICE" ? "active" : ""} onClick={() => resetFilters("CATALOG_PRICE")}><span>目录竞争价</span><small>{catalogRunCount} 次分析</small></button>
      <button role="tab" aria-selected={view === "PRICE_HISTORY"} className={view === "PRICE_HISTORY" ? "active" : ""} onClick={() => resetFilters("PRICE_HISTORY")}><span>价格历史</span><small>{view === "PRICE_HISTORY" ? total.toLocaleString() : "本地 + 官方"}</small></button>
    </div>

    {view === "STOCK_RISK" ? <MetricStrip items={[
      { label: "紧急", value: (summary.CRITICAL ?? 0).toLocaleString(), tone: (summary.CRITICAL ?? 0) ? "warning" : "positive" },
      { label: "高风险", value: (summary.HIGH ?? 0).toLocaleString(), tone: (summary.HIGH ?? 0) ? "warning" : "positive" },
      { label: "最近销量", value: `${String(selectedRun?.parameters.windowDays ?? 90)} 天`, note: "排除取消与无效订单" },
      { label: "库存时间", value: formatDate(latestFreshness), note: selectedRun?.status ?? "尚未分析" },
    ]} /> : view === "CATALOG_PRICE" ? <MetricStrip items={[
      { label: "目录商品", value: (selectedRun?.totalCount ?? 0).toLocaleString() },
      { label: "已读取", value: (selectedRun?.successCount ?? 0).toLocaleString(), tone: "positive" },
      { label: "读取失败", value: (selectedRun?.failedCount ?? 0).toLocaleString(), tone: selectedRun?.failedCount ? "warning" : "positive" },
      { label: "最近更新", value: formatDate(latestFreshness), note: selectedRun?.status ?? "尚未分析" },
    ]} /> : <MetricStrip items={[
      { label: "价格记录", value: total.toLocaleString() },
      { label: "最近记录", value: formatDate(latestFreshness) },
      { label: "数据来源", value: "本地 + 官方" },
      { label: "写入状态", value: "只读", tone: "positive" },
    ]} />}

    {view === "STOCK_RISK" ? <RiskRail summary={summary} selected={status} onSelect={(value) => { setPage(1); setStatus(value); }} /> : null}

    <section className="workspace-band insight-workspace">
      <SectionHeading title={view === "STOCK_RISK" ? "库存覆盖明细" : view === "CATALOG_PRICE" ? "目录竞争价明细" : "价格变动记录"} meta={selectedRun ? `${selectedRun.processedCount.toLocaleString()} / ${selectedRun.totalCount.toLocaleString()} · ${formatDate(selectedRun.startedAt)}` : `${total.toLocaleString()} 条记录`} />
      {selectedRun?.status === "RUNNING" ? <div className="insight-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={currentProgress}><span style={{ width: `${currentProgress}%` }} /><strong>{currentProgress}%</strong></div> : null}
      <div className="filter-bar insight-filter">
        <label className="search-field"><Search size={16} /><input aria-label="搜索 SKU 或商品 ID" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { setPage(1); setQuery(search.trim()); } }} placeholder="SKU / CBT / Item ID" /></label>
        <select aria-label="站点" value={siteCode} onChange={(event) => { setPage(1); setSiteCode(event.target.value); }}><option value="">全部站点</option>{allSites.map((site) => <option key={site} value={site}>{site}</option>)}</select>
        {view === "CATALOG_PRICE" ? <select aria-label="竞争价状态" value={status} onChange={(event) => { setPage(1); setStatus(event.target.value); }}><option value="">全部结果</option><option value="AVAILABLE">有建议价</option><option value="UNAVAILABLE">暂无建议价</option><option value="FAILED">读取失败</option></select> : null}
        {view !== "PRICE_HISTORY" && runs.some((run) => run.kind === view) ? <select aria-label="分析批次" value={selectedRun?.id ?? ""} onChange={(event) => { setPage(1); setSelectedRuns((current) => ({ ...current, [view]: event.target.value })); }}>
          {runs.filter((run) => run.kind === view).map((run) => <option key={run.id} value={run.id}>{formatDate(run.startedAt)} · {run.status}</option>)}
        </select> : null}
        <button className="button secondary" onClick={() => { setPage(1); setQuery(search.trim()); }}>查询</button>
      </div>

      {loading ? <LoadingState /> : view === "STOCK_RISK" ? stockItems.length ? <>
        <div className="table-scroll"><table className="data-table insight-table"><thead><tr><th>SKU / 商品</th><th>站点</th><th>风险</th><th>库存</th><th>窗口销量</th><th>日均销量</th><th>覆盖天数</th><th>价格</th><th>库存时间</th></tr></thead><tbody>
          {stockItems.map((item) => <tr key={`${item.runId}:${item.childItemId}`}><td><strong>{item.merchantSku || "--"}</strong><small>{item.parentItemId} · {item.childItemId}</small><small className="truncate" title={item.title}>{item.title}</small></td><td>{item.siteCode}<small>{item.listingStatus}</small></td><td><span className={`risk-pill ${item.risk.toLowerCase()}`}>{riskLabels[item.risk]}</span></td><td className="mono">{item.availableQuantity.toLocaleString()}</td><td className="mono">{item.unitsSoldWindow.toLocaleString()}</td><td className="mono">{item.velocityPerDay.toFixed(2)}</td><td className="mono coverage-value">{item.daysOfCoverage === null ? "--" : item.daysOfCoverage.toFixed(1)}</td><td className="mono">{formatPrice(item.currencyId, item.price)}</td><td>{formatDate(item.dataFreshAt)}</td></tr>)}
        </tbody></table></div><Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
      </> : <EmptyState title="暂无库存覆盖分析" /> : view === "CATALOG_PRICE" ? catalogItems.length ? <>
        <div className="table-scroll"><table className="data-table insight-table"><thead><tr><th>SKU / 商品</th><th>站点</th><th>当前价格</th><th>建议竞争价</th><th>差额</th><th>竞争状态</th><th>访问份额</th><th>结果</th><th>更新时间</th></tr></thead><tbody>
          {catalogItems.map((item) => { const difference = item.currentPrice !== null && item.priceToWin !== null ? item.priceToWin - item.currentPrice : null; return <tr key={`${item.runId}:${item.childItemId}`}><td><strong>{item.merchantSku || "--"}</strong><small>{item.parentItemId} · {item.childItemId}</small><small className="truncate" title={item.title}>{item.title}</small></td><td>{item.siteCode}</td><td className="mono">{formatPrice("", item.currentPrice)}</td><td className="mono"><strong>{formatPrice("", item.priceToWin)}</strong></td><td className={`mono ${difference !== null && difference < 0 ? "negative-number" : ""}`}>{difference === null ? "--" : `${difference > 0 ? "+" : ""}${difference.toFixed(2)}`}</td><td>{item.status || "--"}</td><td>{item.visitShare || "--"}</td><td>{item.errorCode ? <span className="failure-text" title={item.errorMessage ?? item.errorCode}>{item.errorCode}</span> : <StatusBadge status="SUCCESS" />}</td><td>{formatDate(item.capturedAt)}</td></tr>; })}
        </tbody></table></div><Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
      </> : <EmptyState title="暂无目录竞争价分析" /> : priceItems.length ? <>
        <div className="table-scroll"><table className="data-table insight-table price-history-table"><thead><tr><th>SKU / 商品</th><th>站点</th><th>价格</th><th>来源</th><th>记录时间</th><th><span className="sr-only">操作</span></th></tr></thead><tbody>
          {priceItems.map((item) => <tr key={item.id}><td><strong>{item.merchantSku || "--"}</strong><small>{item.parentItemId} · {item.childItemId}</small></td><td>{item.siteCode}</td><td className="mono"><strong>{formatPrice(item.currencyId, item.price)}</strong></td><td>{priceSourceLabels[item.source]}</td><td>{formatDate(item.observedAt)}</td><td><button className="icon-button" aria-label={`同步 ${item.childItemId} 官方价格历史`} title="同步官方价格历史" disabled={busy !== ""} onClick={() => void syncPriceHistory(item)}>{busy === item.id ? <LoaderCircle size={16} className="spin" /> : <History size={16} />}</button></td></tr>)}
        </tbody></table></div><Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
      </> : <EmptyState title="暂无价格记录" />}
    </section>
  </>;
}
